import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, batch } from 'react-redux';
import { useAppDispatch } from '../../reduxService/hooks';
import { AppDispatch, RootState, redux } from '../../reduxService';
import { setAASHTOResults, setProject, setHasBeenRan, setIsNavigatingToResults } from '../Dynamic/store/project/projectslice';
import {
    requestSubmit,
    clearLastSubmitResult,
    clearSubmissionErrors,
    setAccordionToOpen,
    submitFailed,
    markSubmitSource,
    releaseSubmitSource,
    setSaveNotification,
} from '../store/form/formControlSlice';
import { calculationServiceFacade } from '../services/CalculationServiceFacade';
import { EProjectType } from '../enum/project';
import { IPavedData, IProject, ProjectData } from '../api/IApiResponse';
import { logDebug, logError, LogTag } from '../utils/utils';
import { saveProjectToBackend } from '../services/ProjectApiService';
import { BackendPayloadService } from '../services/BackendPayloadService';
import { WireValueError } from '../services/WireValueNormalizer';
import { validateReduxStructure } from '../types/ProjectDataStructure';
import { handleValidationErrors } from '../Dynamic/utils/formSubmissionHelpers';
import { holdsImperialDisplayValues } from '../utils/conversionUtils';

// Navigation coordination timeout
// This delay allows React Router to complete route transition and useFormEffects
// to see the new path before clearing the isNavigatingToResults flag
const NAVIGATION_STABILIZATION_DELAY_MS = 500;

/**
 * RUN guard for the frontend Execute (run-text-fields C3): a malformed value stops the calculation, the
 * navigation and the save; the Stepper Dialog shows it through submitFailed (the caller does not await).
 * Otherwise it resolves to the normalized copy the frontend calculation reads: the form stores numbers as text
 * (FormattedInput), while Redux and the save keep the form values.
 */
type RunValuesCheck = { rejected: true } | { rejected: false; normalizedData: ProjectData | undefined };

const checkRunValues = async (project: IProject, dispatch: AppDispatch): Promise<RunValuesCheck> => {
    try {
        return { rejected: false, normalizedData: await BackendPayloadService.assertRunPayload(project) };
    } catch (error) {
        if (!(error instanceof WireValueError)) throw error;
        dispatch(submitFailed({ error }));
        return { rejected: true };
    }
};

// Type definitions
type ProjectDataType = any; // This should be properly typed based on your project structure
type IndexableProjectData = { [key: string]: any };

interface UseCalculationOrchestratorResult {
    handleExecuteAction: () => Promise<void>;
    handleSave: () => void;
}

/**
 * Custom hook for orchestrating project calculations
 *
 * This hook handles:
 * - Frontend calculations (TypeScript): paved, airport projects using PavedCalculationOrchestrator
 * - Backend calculations (C# ASP.NET): unpaved, container_yard, parking_deck, railways, working_platforms
 * - Form submission coordination
 * - Navigation to results
 * - Airport to Paved data transformation for calculation compatibility
 */
export const useCalculationOrchestrator = (
    dynamicProjectData: { guid?: string; hasBeenRan?: boolean; projectInfo?: { data?: any }; method?: string } | null,
    formikRef?: React.RefObject<any>
): UseCalculationOrchestratorResult => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Redux selectors
    const dynamicProject = useSelector((state: RootState) => state.Dynamic.project);
    const isFormSubmitting = useSelector((state: RootState) => state.formControl.isSubmitting);
    const isApiSaving = false; // This property doesn't exist in the current state structure

    /**
     * Transforms Airport project data to Paved project data for calculation compatibility.
     *
     * ARCHITECTURAL NOTE: Only Airport and Paved projects use frontend TypeScript calculations
     * via PavedCalculationOrchestrator. Airport projects are structurally identical to Paved
     * projects but need type conversion to use the same calculation engine.
     */
    const transformAirportToPavedData = useCallback((data: ProjectDataType): IPavedData => {
        if (data.type === EProjectType.airport) {
            // Create a copy and change the type to paved for calculation compatibility
            return {
                ...data,
                type: EProjectType.paved,
            } as IPavedData;
        }
        // If it's already paved or another type, return as-is
        return data as IPavedData;
    }, []);

    // Handle save action
    const handleSave = useCallback(() => {
        logDebug('useCalculationOrchestrator', 'handleSave triggered. Dispatching requestSubmit...', undefined, LogTag.FORM_SUBMISSION);
        dispatch(requestSubmit({ source: 'ButtonSave' }));
    }, [dispatch]);

    // Main calculation orchestration function
    const handleExecuteAction = useCallback(async () => {
        logDebug('useCalculationOrchestrator', 'handleExecuteAction triggered.', undefined, LogTag.UI_INTERACTIONS);
        dispatch(clearLastSubmitResult());
        dispatch(clearSubmissionErrors());
        dispatch(setAccordionToOpen(null));

        // Frontend calculations (TypeScript): paved, airport
        // These projects use PavedCalculationOrchestrator for client-side AASHTO calculations
        if (dynamicProject.data?.type === EProjectType.paved || dynamicProject.data?.type === EProjectType.airport) {
            // 🔧 FIX (BUG-REGRESSION-PAVED): Removed dispatch(requestSubmit()) for Paved/Airport
            // REASON: Paved/Airport do FRONTEND calculations and handle their own save in orchestrator
            // requestSubmit was triggering useProjectSubmission.sendProjectData which expects
            // backend-calculated response with {projectInfo: {data: {...}}} structure
            // For Paved/Airport, backend save returns simple acknowledgment, causing crash

            // 🚀 FIX: Validate and sync Formik → Redux before Execute if form has unsaved changes
            // REASON: Execute reads from Redux, but unsaved changes are only in Formik
            // Without this, Execute uses OLD values (e.g., old geogrid selection)
            if (formikRef?.current) {
                logDebug('useCalculationOrchestrator', 'Validating form before Execute...', undefined, LogTag.FORM_VALIDATION);

                // Validate form BEFORE submitting
                const errors = await formikRef.current.validateForm();

                if (Object.keys(errors).length > 0) {
                    // Validation failed - show errors and stop execution
                    logError('useCalculationOrchestrator', 'Form validation failed, blocking execution', errors, LogTag.FORM_VALIDATION);
                    handleValidationErrors(errors, formikRef);
                    dispatch(submitFailed({ validationErrors: errors }));
                    return; // Don't proceed with calculation
                }

                // Validate the raw candidate (Redux + current Formik values, merged as InputForm does) before
                // submitForm writes it to Redux and the calculation reads it.
                const reduxProject = redux.getState()?.Dynamic?.project?.data;
                if (reduxProject) {
                    const existingData: Record<string, unknown> = { ...(reduxProject.projectInfo?.data ?? {}) };
                    if (reduxProject.method) delete existingData[reduxProject.method];
                    const candidate = { ...reduxProject, projectInfo: { ...reduxProject.projectInfo, data: { ...existingData, ...formikRef.current.values } as ProjectData } };
                    if ((await checkRunValues(candidate, dispatch)).rejected) return;
                }

                // Validation passed - safe to submit and sync to Redux
                logDebug('useCalculationOrchestrator', 'Syncing Formik to Redux before Execute...', undefined, LogTag.FORM_SUBMISSION);
                // The sync must not start the Stepper's save: the background save after the calculation is the only one.
                dispatch(markSubmitSource('ExecuteSync'));
                try {
                    await formikRef.current.submitForm();
                } finally {
                    dispatch(releaseSubmitSource('ExecuteSync'));
                }

                // ✅ FIX #2.3: Wait for Redux to actually update (not just a fixed 50ms delay)
                // This prevents reading stale data on slow devices or with heavy Redux middleware
                // ✅ FIX #1: Guard flag prevents race condition where both callbacks fire
                await new Promise<void>((resolve) => {
                    let resolved = false;
                    const unsubscribe = redux.subscribe(() => {
                        if (!resolved) {
                            resolved = true;
                            unsubscribe();
                            resolve();
                        }
                    });
                    // Safety timeout - don't wait forever if Redux doesn't update
                    setTimeout(() => {
                        if (!resolved) {
                            resolved = true;
                            unsubscribe();
                            resolve();
                        }
                    }, 500);
                });
            }

            logDebug('useCalculationOrchestrator', 'Getting current project data for frontend calculation...', undefined, LogTag.FORM_SUBMISSION);

            // Get the FRESH project data from Redux (re-select from current state)
            // This ensures we get the most up-to-date data after form submission
            const freshProjectData = redux.getState()?.Dynamic?.project?.data;
            const runValues = freshProjectData ? await checkRunValues(freshProjectData, dispatch) : undefined;
            if (runValues?.rejected) return;
            const normalizedRunData = runValues?.normalizedData as IndexableProjectData | undefined;

            // Handle the BackendPayloadService structure where method data is nested
            // For Airport projects, the data is now under projectInfo.data.asphalt
            let projectDataForCalc: ProjectDataType | undefined;
            let projectDataForSave: ProjectDataType | undefined;
            if (freshProjectData?.projectInfo?.data && normalizedRunData) {
                const rawData = freshProjectData.projectInfo.data as IndexableProjectData;
                const method = freshProjectData.method || 'asphalt'; // Airport projects use 'asphalt' method

                console.log('🔍 DEBUG: Fresh project data structure', {
                    projectType: freshProjectData.type,
                    method,
                    hasProjectInfo: !!freshProjectData.projectInfo,
                    hasProjectInfoData: !!freshProjectData.projectInfo?.data,
                    rawDataKeys: Object.keys(rawData),
                    hasMethodData: !!rawData[method],
                    baseCourseInRawData: !!(rawData as any).baseCourse?.baseGeogrid,
                    baseCourseInMethodData: !!(rawData[method] as any)?.baseCourse?.baseGeogrid,
                });

                // FLAT vs NESTED structure handling
                // PRIMARY: FLAT structure (post-transformProjectFromBackend) - data at root level
                // FALLBACK: NESTED structure (edge cases) - data under [method] key
                const methodData = rawData[method];
                if (methodData && typeof methodData === 'object') {
                    // NESTED structure detected - flatten it before calculations
                    // This is a safety fallback; transformProjectFromBackend should already flatten
                    console.warn('⚠️ NESTED structure detected at runtime - this should not happen if transformProjectFromBackend() worked correctly', {
                        projectType: freshProjectData.type,
                        method,
                        rawDataKeys: Object.keys(rawData),
                        hint: 'Check transformProjectFromBackend() or ensureFlatStructure() in the data loading pipeline',
                    });
                    projectDataForSave = { ...rawData, ...(methodData as object) };
                    projectDataForCalc = { ...normalizedRunData, ...(normalizedRunData[method] as object) };

                    console.log('🔍 DEBUG: NESTED structure detected, flattening', {
                        projectType: freshProjectData.type,
                        hasBaseCourse: !!(projectDataForCalc as any).baseCourse,
                        baseGeogrid: (projectDataForCalc as any).baseCourse?.baseGeogrid,
                        note: 'Data extracted from method key and merged with root',
                    });
                } else {
                    // FLAT structure (expected case) - use data directly
                    projectDataForSave = rawData;
                    projectDataForCalc = normalizedRunData;

                    console.log('🔍 DEBUG: FLAT structure detected, using directly', {
                        projectType: freshProjectData.type,
                        hasBaseCourse: !!(projectDataForCalc as any).baseCourse,
                        baseGeogrid: (projectDataForCalc as any).baseCourse?.baseGeogrid,
                        note: 'Data at root level (correct FLAT structure)',
                    });
                }

                // 🔧 FIX (BUG-REGRESSION-PAVED-RELOAD): Validate AFTER denesting
                // REASON: Validation must check the final flattened structure, not the raw structure
                // Moving validation here ensures we validate the actual data we'll use for calculations
                if (projectDataForCalc) {
                    validateReduxStructure(projectDataForCalc as any, method, 'useCalculationOrchestrator');
                }

                // Apply Airport to Paved transformation for calculation compatibility
                // Airport projects are structurally identical to Paved but need type conversion
                if (projectDataForCalc) {
                    const beforeTransform = (projectDataForCalc as any).baseCourse?.baseGeogrid;
                    projectDataForCalc = transformAirportToPavedData(projectDataForCalc);
                    const afterTransform = (projectDataForCalc as any).baseCourse?.baseGeogrid;

                    console.log('🔍 DEBUG: After Airport→Paved transformation', {
                        beforeType: freshProjectData.type,
                        afterType: (projectDataForCalc as any).type,
                        baseGeogridBefore: beforeTransform,
                        baseGeogridAfter: afterTransform,
                        preserved: beforeTransform === afterTransform,
                    });
                }
            }

            // CRITICAL FIX: Check if we have the necessary data for calculation
            // Previously checked for 'asphalt' key which doesn't exist in flat structure
            // Now check for required calculation data (trafficLoad is mandatory for Paved/Airport)
            if (projectDataForCalc && projectDataForCalc.trafficLoad) {
                // Phase 3: Use CalculationServiceFacade for unified calculation API
                // Single entry point with internal service coordination, caching, and error handling
                logDebug('useCalculationOrchestrator', 'Using CalculationServiceFacade for complete calculation management', undefined, LogTag.CALCULATIONS);

                console.log('🔍 DEBUG: Data passed to calculation (COMPLETE)', {
                    projectType: (projectDataForCalc as any).type,
                    trafficLoad: {
                        esalFinalValue: (projectDataForCalc as any).trafficLoad?.esalFinalValue,
                        reliabilityLevel: (projectDataForCalc as any).trafficLoad?.reliabilityLevel,
                        roadType: (projectDataForCalc as any).trafficLoad?.roadType,
                    },
                    materials: {
                        baseMaterial: (projectDataForCalc as any).baseCourse?.baseMaterial,
                        subbaseMaterial: (projectDataForCalc as any).subbaseCourse?.subbaseMaterial,
                    },
                    geogrids: {
                        baseGeogrid: (projectDataForCalc as any).baseCourse?.baseGeogrid,
                        subbaseGeogrid: (projectDataForCalc as any).subbaseCourse?.subbaseGeogrid,
                    },
                    cbr: {
                        baseCbr: (projectDataForCalc as any).baseCourse?.cbrBaseCourse,
                        subbaseCbr: (projectDataForCalc as any).subbaseCourse?.cbrSubbaseCourse,
                        subgradeCbr: (projectDataForCalc as any).subgrade?.cbrSubgrade,
                    },
                    minThickness: {
                        base: (projectDataForCalc as any).baseCourse?.minThickness,
                        subbase: (projectDataForCalc as any).subbaseCourse?.minThickness,
                    },
                });

                try {
                    const result = await calculationServiceFacade.executeCompleteCalculation(projectDataForCalc as IPavedData);

                    console.log('🔍 DEBUG: Calculation results', {
                        projectType: (projectDataForCalc as any).type,
                        base: {
                            stabilised: result.layerAnalysis.layerThicknesses?.base?.stabilised,
                            unstabilised: result.layerAnalysis.layerThicknesses?.base?.unstabilised,
                        },
                        subbase: {
                            stabilised: result.layerAnalysis.layerThicknesses?.subbase?.stabilised,
                            unstabilised: result.layerAnalysis.layerThicknesses?.subbase?.unstabilised,
                        },
                        note: 'stabilised should be LESS than unstabilised when geogrid present',
                    });

                    // Transform facade result to Redux expected format
                    const reduxCompatibleResults = {
                        totalSN: result.totalSN,
                        finalSN: result.totalSN, // Use totalSN as finalSN since we don't have separate finalSN calculation
                        verification: {
                            isAdequate: true, // For now, assume adequate since we're successfully calculating
                            message: 'Design calculated successfully.',
                        },
                        requiredThicknesses: {
                            surface: result.layerAnalysis.layerThicknesses?.surface?.unstabilised || 0.1,
                            base: result.layerAnalysis.layerThicknesses?.base?.unstabilised || 0.2,
                            subbase: result.layerAnalysis.layerThicknesses?.subbase?.unstabilised || 0.15,
                        },
                        layerAnalysis: result.layerAnalysis,
                        detailedCalculations: result.detailedCalculations,
                    };

                    logDebug('useCalculationOrchestrator', 'Service facade calculation completed:', {
                        cacheHit: result.cacheHit,
                        calculationTimeMs: result.calculationTimeMs,
                        totalSN: result.totalSN,
                    });

                    console.log('🔍 DEBUG: About to dispatch to Redux', {
                        baseValuesInResult: {
                            stabilised: result.detailedCalculations?.softwareDesign?.base?.stabilised,
                            unstabilised: result.detailedCalculations?.softwareDesign?.base?.unstabilised,
                        },
                        totalSN: {
                            stabilised: result.detailedCalculations?.softwareDesign?.totalSN?.stabilised,
                            unstabilised: result.detailedCalculations?.softwareDesign?.totalSN?.unstabilised,
                        },
                        note: 'These are the values being sent to Redux via setAASHTOResults',
                    });

                    // Dispatch results to Redux store using correct action
                    dispatch(setAASHTOResults(reduxCompatibleResults));
                } catch (error) {
                    logError('useCalculationOrchestrator', 'Service facade calculation failed:', error, LogTag.CALCULATIONS);
                    throw error; // Let the existing error handling manage this
                }
            }

            // Save project to backend after calculation (silently)
            // CRITICAL FIX: Use projectDataForSave (the flat form values, including geogrid)
            // instead of re-reading from Redux (which may have nested/incomplete structure)
            const currentReduxState = redux.getState()?.Dynamic?.project?.data;

            // CRITICAL FIX: Use flat structure for BOTH Airport and Paved
            // projectDataForSave is already flat for both types (the nested fallback is flattened above)
            // Results_Airport and Results_Paved both read from projectInfo.data directly (flat, not nested)

            // BUG FIX: Preserve original project type (Airport → Paved transformation is ONLY for calculations)
            // The transformation should NOT be persisted to Redux or backend
            const originalProjectType = currentReduxState?.type; // Preserve 'airport' if it was Airport

            const projectToSave =
                currentReduxState && currentReduxState.guid
                    ? {
                          ...currentReduxState,
                          hasBeenRan: true, // 🔧 FIX: Prevent state machine redirect after calculation
                          projectInfo: {
                              ...currentReduxState.projectInfo,
                              data: {
                                  ...projectDataForSave, // Flat form values: Redux and the save keep what the form stored
                                  type: originalProjectType, // ✅ Restore original type (airport/paved)
                              },
                          },
                      }
                    : undefined;

            console.log('🔍 DEBUG: Project to save structure', {
                hasProjectToSave: !!projectToSave,
                originalType: originalProjectType,
                projectTypeInData: (projectToSave?.projectInfo?.data as any)?.type,
                projectDataForCalcType: (projectDataForCalc as any)?.type,
                hasGuid: !!projectToSave?.guid,
                baseCourseInProjectToSave: !!(projectToSave?.projectInfo?.data as any)?.baseCourse?.baseGeogrid,
                note: 'Original type preserved (Airport fix)',
                // 🔍 DEBUG: Check surface.minThickness values for form reset bug
                surfaceMinThicknessInProjectDataForCalc: (projectDataForCalc as any)?.surface?.minThickness,
                surfaceMinThicknessInCurrentRedux: (currentReduxState?.projectInfo?.data as any)?.surface?.minThickness,
                surfaceMinThicknessInProjectToSave: (projectToSave?.projectInfo?.data as any)?.surface?.minThickness,
            });

            // Navigate to results page after dispatching
            console.log('🔍 DEBUG: Checking navigation condition', {
                hasDynamicProject: !!dynamicProject,
                hasDynamicProjectData: !!dynamicProject?.data,
                guid: dynamicProject?.data?.guid,
                willNavigate: !!dynamicProject?.data?.guid,
            });

            if (dynamicProject?.data?.guid) {
                console.log('🔍 DEBUG: Preparing navigation to results', { guid: dynamicProject.data.guid });

                // 🔧 CRITICAL FIX (v5.2.0): Batch all Redux dispatches to prevent race condition
                // REASON: Separate dispatches caused useProjectFormSetupModular effect to trigger
                // with STALE data when hasBeenRan changed, before setProject() updated the actual data.
                // This caused form values (like surface.minThickness) to reset to defaults.
                // SOLUTION: Use react-redux batch() to ensure all state changes happen atomically.
                // React will only re-render once with the complete state.
                //
                // 🔧 ORDER MATTERS: setProject() replaces entire state.data, so:
                // 1. setProject() first (updates project data including hasBeenRan)
                // 2. setIsNavigatingToResults() AFTER (so flag is NOT overwritten by setProject)
                batch(() => {
                    // 1. Update full project data first (includes hasBeenRan: true in projectToSave)
                    if (projectToSave && projectToSave.guid) {
                        dispatch(setProject(projectToSave));
                    }
                    // 2. THEN set navigation flag (after setProject, so it's not overwritten)
                    dispatch(setIsNavigatingToResults(true));
                });

                // 🚀 SIMPLIFIED: Reset state machine to CLEAN when hasBeenRan=true
                // GUARANTEES: hasBeenRan=true → state=CLEAN → canRedirect=false → NO redirect
                // Centralizes logic in one place instead of manual transitions everywhere
                const formStateManagementService = require('../services/FormStateManagementService').formStateManagementService;
                formStateManagementService.forceReset();
                console.log('🔍 DEBUG: State machine reset to CLEAN (hasBeenRan=true, prevent unwanted redirect)');

                console.log('🔍 DEBUG: Redux updated with complete project data (batched)', {
                    hasBaseCourseInRedux: !!(projectToSave?.projectInfo?.data as any)?.baseCourse?.baseGeogrid,
                    timing: 'BATCHED with navigation flags',
                    note: 'All dispatches atomic - prevents form reset race condition',
                });
            } else if (projectToSave && projectToSave.guid) {
                // Fallback: Update project data even without navigation
                dispatch(setProject(projectToSave));
                console.log('🔍 DEBUG: Redux updated (no navigation)', {
                    hasGuid: !!projectToSave.guid,
                });
            }

            // 🔧 CRITICAL FIX: Navigate IMMEDIATELY after Redux updates, BEFORE backend save
            // REASON: await saveProjectToBackend() takes time → React re-renders → redirect can happen
            // SOLUTION: Navigate first, then save in background (non-blocking)
            if (dynamicProject?.data?.guid) {
                console.log('🔍 DEBUG: Navigating to results BEFORE backend save');
                navigate(`/results/${dynamicProject.data.guid}`);
                console.log('🔍 DEBUG: Navigate called - should see URL change now');

                // ✅ FIX: Navigation flag is now cleared in Results component to avoid race conditions
                // This prevents the flag from being cleared before the route actually changes on slow devices
                // setTimeout(() => dispatch(setIsNavigatingToResults(false)), NAVIGATION_STABILIZATION_DELAY_MS);
            } else {
                console.log('🔍 DEBUG: Navigation skipped - no guid found');
            }

            // Save to backend in background (after navigation, non-blocking)
            if (projectToSave && projectToSave.guid) {
                // Fix R6S2-1: Reset conversionApplied so orchestratePayload converts Imperial→SI
                // only while Redux still holds display values (same guard as useProjectSubmission.ts)
                const projectForBackend = holdsImperialDisplayValues(projectToSave)
                    ? { ...projectToSave, conversionApplied: false }
                    : projectToSave;
                const notifyBackgroundSaveFailed = () =>
                    dispatch(
                        setSaveNotification({
                            show: true,
                            type: 'error',
                            message: `Calculation completed, but project "${projectToSave.projectName || projectToSave.guid}" could not be saved. Use Save to retry.`,
                        })
                    );
                // Fire-and-forget backend save (don't await)
                saveProjectToBackend(projectForBackend)
                    .then(saveResult => {
                        if (saveResult.success) {
                            logDebug(
                                'useCalculationOrchestrator',
                                '✅ Project saved to backend successfully (background)',
                                {
                                    status: saveResult.status,
                                    guid: projectToSave.guid,
                                },
                                LogTag.API_CALLS
                            );
                        } else {
                            logError(
                                'useCalculationOrchestrator',
                                '⚠️ Failed to save project to backend (background, non-blocking)',
                                {
                                    error: saveResult.error,
                                    status: saveResult.status,
                                    guid: projectToSave.guid,
                                },
                                LogTag.API_CALLS
                            );
                            notifyBackgroundSaveFailed();
                        }
                    })
                    .catch(error => {
                        // Log error but don't block navigation
                        logError('useCalculationOrchestrator', '⚠️ Exception during background save (non-blocking)', error, LogTag.API_CALLS);
                        notifyBackgroundSaveFailed();
                    });
            }
        }
        // Backend calculations (C# ASP.NET): unpaved, container_yard, parking_deck, railways, working_platforms
        // These projects use server-side calculation APIs with backend payload handling
        else if (dynamicProjectData?.hasBeenRan && dynamicProjectData.guid) {
            logDebug('useCalculationOrchestrator', 'Navigating to existing results', undefined, LogTag.NAVIGATION);
            navigate(`/results/${dynamicProjectData.guid}`);
        } else if (!isFormSubmitting && !isApiSaving) {
            // Backend projects: dispatch standard submit request for API-based calculations
            logDebug('useCalculationOrchestrator', 'Dispatching submit request for backend calculation', undefined, LogTag.API_CALLS);
            dispatch(requestSubmit({ source: 'ButtonExecute' }));
        }
    }, [dispatch, dynamicProject, dynamicProjectData, navigate, transformAirportToPavedData, isFormSubmitting, isApiSaving]);

    return {
        handleExecuteAction,
        handleSave,
    };
};
